import { useAppDispatch, useAppSelector } from 'store';

import type { TableProps } from 'antd';
import type {
  IQueueTableData,
  TTableName,
  TTablePrefix,
} from 'infrastructure/interfaces';
import type { SortState } from 'infrastructure/types';

const QUEUE_LIMIT = 10;

type TKeyof<T> = keyof T;

interface Props<TFilter extends Record<string, any>> {
  filter: TFilter;
  updateFilter: (filter: Partial<TFilter>) => void;
  excludeSortFields: (keyof TFilter)[];
  tablePrefix: TTablePrefix;
  tableName: TTableName;
}

export const useConsoleTable = <
  TFilter extends Record<string, any>,
  TData extends IQueueTableData,
>({
  filter,
  updateFilter,
  excludeSortFields,
  tablePrefix,
  tableName,
}: Props<TFilter>) => {
  const workQueue = useAppSelector((state) => state.ccmConsole.workQueue);
  const selectedRows = useAppSelector((state) => state.ccmConsole.selectedRows);
  const {
    ccmConsole: { addToWorkQueue, setSelectedRows },
  } = useAppDispatch();

  const alreadyInQueueItems = workQueue.filter(
    (i) => i.tableName === tableName,
  );

  const queueLimitReached = workQueue.length >= QUEUE_LIMIT;
  const selectedLimitReached =
    selectedRows.length + workQueue.length >= QUEUE_LIMIT;
  const isAllSelectedInQueue = selectedRows
    .filter((i) => i.tableName === tableName)
    .every((i) => alreadyInQueueItems.some((j) => j.guid === i.guid));
  const disableAddToQueue = queueLimitReached || isAllSelectedInQueue;

  const rowSelection: TableProps<TData>['rowSelection'] = {
    onChange: (_key, rows, info) => {
      const filteredSelectedRowsByTableName = selectedRows.filter(
        (i) => i.tableName !== tableName,
      );

      let filteredRowsByAlreadyInQueueItems = rows.filter(
        (r) => !alreadyInQueueItems.some((j) => j.guid === r.guid),
      );

      if (info.type === 'all' && filteredRowsByAlreadyInQueueItems.length > 0) {
        const freeSlots =
          QUEUE_LIMIT -
          (workQueue.length + filteredSelectedRowsByTableName.length);
        filteredRowsByAlreadyInQueueItems =
          filteredRowsByAlreadyInQueueItems.slice(0, freeSlots);
      }

      const newSelectedRows = [
        ...filteredSelectedRowsByTableName,
        ...filteredRowsByAlreadyInQueueItems,
      ];
      setSelectedRows(newSelectedRows);
    },
    getCheckboxProps: (record) => {
      const alreadySelected = selectedRows.some(
        (i) => i.guid === record.guid && i.tableName === tableName,
      );
      const alreadyExistInQueue = workQueue.some(
        (i) => i.guid === record.guid && i.tableName === tableName,
      );
      const disabled =
        queueLimitReached ||
        alreadyExistInQueue ||
        (!alreadySelected && selectedLimitReached);

      return {
        disabled,
        'data-cy': `${tableName}-row-checkbox-${record.guid}`,
      };
    },

    selectedRowKeys: [
      ...selectedRows
        .filter((i) => i.tableName === tableName)
        .map((i) => i.guid),
      ...alreadyInQueueItems
        .filter((i) => i.tableName === tableName)
        .map((i) => i.guid),
    ],
  };

  const pushToQueue = () => {
    const alreadyHasThisPatient = workQueue.some((i) =>
      selectedRows.some((j) => i.guid === j.guid && i.tableName === tableName),
    );

    if (alreadyHasThisPatient) {
      const filteredSelectedRows = selectedRows.filter(
        (i) =>
          !workQueue.some(
            (j) => i.guid === j.guid && j.tableName === tableName,
          ),
      );

      addToWorkQueue(
        filteredSelectedRows.filter((i) => i.tableName === tableName),
      );
      setSelectedRows(
        filteredSelectedRows.filter((i) => i.tableName !== tableName),
      );
      return;
    }

    addToWorkQueue(selectedRows.filter((i) => i.tableName === tableName));
    setSelectedRows(selectedRows.filter((i) => i.tableName !== tableName));
  };

  const onSort = (field: string, sort: SortState) => {
    const pageKey: TKeyof<TFilter> = `${tablePrefix}page`;

    const cleanedFilter = Object.keys(filter).reduce<Partial<TFilter>>(
      (acc, key) => {
        const typedKey = key as TKeyof<TFilter>;

        if (excludeSortFields.includes(typedKey)) return acc;

        acc[typedKey] = undefined;
        return acc;
      },
      {},
    );

    const nextFilter: Partial<TFilter> = {
      ...cleanedFilter,
      [pageKey]: 1,
      [`${tablePrefix}${field}`]: sort ?? undefined,
    };

    updateFilter(nextFilter);
  };

  return { onSort, pushToQueue, rowSelection, disableAddToQueue };
};
